import { Button, Card, Col, Progress, Row, Skeleton, Modal, Popover, Typography, message, Space, Switch } from 'antd'
import React, { useEffect, useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import GoogleChartOrEmpty from '../common/GoogleChartOrEmpty'
import {
  BankOutlined,
  BarChartOutlined,
  ClockCircleOutlined,
  CreditCardOutlined,
  DollarCircleOutlined,
  FileTextOutlined,
  LineChartOutlined,
  SettingOutlined
} from '@ant-design/icons'
import '../../asserts/css/custom.css'
import Certificate from '../../assets/img/certificate.svg'
import CheckBook from '../../assets/img/check-book.svg'
import FileChart from '../../assets/img/file-chart.svg'
import InstitutionUserService from '../../services/InstitutionUserService'
import {
  buildFinanceKpiPipelineCompareTrend,
  buildFinanceKpiSingleSeriesTrend,
  generateChartDataMFinance,
  generateChartRefundDataMFinance
} from '../../utils/generateChartData'

interface DataType {
  key: React.Key
  amount: number
  reason: string
  status: string
  created_at: string
  updated_at: string
  column: string
  totalPayments: string
  amountRequestedToBeRefund: string
  amountRequestedToBeRefundedInProgressStatus: string
  TotalNumberOfApplicantRequestedRefund: string
  notRequestedBidders: string
}
export const barOptions = {
  chart: {
    title: 'Bid Security Provided Per Month - All Years',
    subtitle: 'Amount Paid for Bid Security by Year and Month'
  },
  curveType: 'function',
  pointSize: 4,
  lineWidth: 2
}

export const barOptions2 = {
  chart: {
    title: 'Digital Refunded Amounts per Month',
    subtitle: 'Refunded Amount - 2023-Now'
  },
  curveType: 'function',
  pointSize: 4,
  lineWidth: 2
}

type FinanceKpiTrendKey = 'refunded' | 'pipeline' | 'requested'

type FinanceKpiTrendChartVariant = 'popover' | 'modal'

const TREND_MODAL_META: Record<
FinanceKpiTrendKey,
{ title: string, description: string }
> = {
  refunded: {
    title: 'Total refunded — monthly trend',
    description:
      'Amount completed each month (refund requests with status "Downloaded to be Refunded", bidders and bailiffs combined). Matches the headline total when summed over all periods.'
  },
  pipeline: {
    title: 'Pipeline — requested vs completed',
    description:
      'Orange: total refund amount requested or updated in the month. Green: amount completed in the month. The KPI "pending" is a point-in-time snapshot; this chart shows how inflow and outflow move over time.'
  },
  requested: {
    title: 'Refund request volume — monthly trend',
    description:
      'Total amount tied to refund activity in the month (combined bidder and bailiff requests by update time). Aligns with "Total requested for refund" at the aggregate level.'
  }
}

function buildFinanceKpiColumnOptions(
  key: FinanceKpiTrendKey,
  variant: FinanceKpiTrendChartVariant
): Record<string, unknown> {
  const chartArea =
    variant === 'modal'
      ? { backgroundColor: 'transparent', left: 72, right: 28, top: 44, bottom: key === 'pipeline' ? 92 : 64 }
      : { backgroundColor: 'transparent', left: 48, right: 10, top: 36, bottom: key === 'pipeline' ? 78 : 54 }

  const base: Record<string, unknown> = {
    responsive: true,
    maintainAspectRatio: false,
    backgroundColor: 'transparent',
    chartArea,
    hAxis: {
      title: 'Month',
      slantedText: true,
      slantedTextAngle: variant === 'modal' ? 42 : 36,
      textStyle: { fontSize: variant === 'modal' ? 12 : 10 },
      titleTextStyle: { fontSize: 12, color: '#64748b' }
    },
    vAxis: {
      minValue: 0,
      format: 'decimal',
      textStyle: { fontSize: variant === 'modal' ? 12 : 10 }
    },
    bar: { groupWidth: variant === 'modal' ? '72%' : '70%' }
  }

  if (key === 'refunded') {
    return {
      ...barOptions2,
      ...base,
      chart: {
        title: variant === 'modal' ? 'Refunded amount (RWF)' : 'Refunded (RWF)',
        subtitle: 'By month — completed refunds'
      },
      colors: ['#059669'],
      legend: { position: 'none' }
    }
  }
  if (key === 'requested') {
    return {
      ...barOptions2,
      ...base,
      chart: {
        title: variant === 'modal' ? 'Refund request volume (RWF)' : 'Requested (RWF)',
        subtitle: 'By month — request activity'
      },
      colors: ['#4f46e5'],
      legend: { position: 'none' }
    }
  }
  return {
    ...barOptions2,
    ...base,
    chart: {
      title: variant === 'modal' ? 'Requested vs completed (RWF)' : 'Pipeline (RWF)',
      subtitle: 'Requested (orange) vs completed (green)'
    },
    colors: ['#d97706', '#059669'],
    legend: { position: 'bottom', textStyle: { fontSize: variant === 'modal' ? 12 : 10 } }
  }
}

const { Text, Title } = Typography

const KPI_ACCENTS = {
  emerald: {
    ring: 'rgba(16, 185, 129, 0.35)',
    iconBg: 'linear-gradient(160deg, #ecfdf5 0%, #d1fae5 100%)',
    icon: '#059669',
    value: '#047857',
    progress: { '0%': '#34d399', '100%': '#059669' }
  },
  amber: {
    ring: 'rgba(245, 158, 11, 0.45)',
    iconBg: 'linear-gradient(160deg, #fffbeb 0%, #fde68a 100%)',
    icon: '#d97706',
    value: '#b45309',
    progress: { '0%': '#fcd34d', '100%': '#d97706' }
  },
  indigo: {
    ring: 'rgba(99, 102, 241, 0.35)',
    iconBg: 'linear-gradient(160deg, #eef2ff 0%, #e0e7ff 100%)',
    icon: '#4f46e5',
    value: '#4338ca',
    progress: { '0%': '#a5b4fc', '100%': '#4f46e5' }
  },
  forest: {
    ring: 'rgba(55, 118, 47, 0.45)',
    iconBg: 'linear-gradient(160deg, #f0fdf4 0%, #dcfce7 100%)',
    icon: '#37762f',
    value: '#1e4620',
    progress: { '0%': '#86efac', '100%': '#37762f' }
  }
} as const

type KpiAccentKey = keyof typeof KPI_ACCENTS

interface FinanceKpiStatCardProps {
  accent: KpiAccentKey
  icon: React.ReactNode
  category: string
  title: string
  value: string
  percent: number | null
  percentHint: string
  loading: boolean
  showProgress?: boolean
  action?: React.ReactNode
}

function FinanceKpiStatCard(props: FinanceKpiStatCardProps): JSX.Element {
  const {
    accent,
    icon,
    category,
    title,
    value,
    percent,
    percentHint,
    loading,
    showProgress = true,
    action
  } = props
  const c = KPI_ACCENTS[accent]
  const pct = percent !== null && Number.isFinite(percent) ? Math.min(100, Math.max(0, percent)) : 0

  return (
    <Card
      className="stats-card h-full finance-stat-card finance-kpi-stat-card"
      style={{
        background: 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        borderRadius: '14px',
        border: '1px solid rgba(148, 163, 184, 0.35)',
        boxShadow: '0 4px 14px rgba(15, 23, 42, 0.06)',
        color: '#0f172a'
      }}
      hoverable
      bodyStyle={{ padding: '22px 18px 18px' }}
    >
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 8,
          minHeight: showProgress ? 200 : 188
        }}
      >
        <div
          aria-hidden
          style={{
            background: c.iconBg,
            borderRadius: '50%',
            width: 56,
            height: 56,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: `2px solid ${c.ring}`,
            boxShadow: '0 6px 16px rgba(15, 23, 42, 0.08)',
            marginBottom: 2
          }}
        >
          <span style={{ display: 'inline-flex', color: c.icon, fontSize: 26 }}>{icon}</span>
        </div>
        <Text style={{ fontSize: 11, letterSpacing: '0.08em', textTransform: 'uppercase', color: '#64748b', fontWeight: 700 }}>
          {category}
        </Text>
        <Title level={5} style={{ margin: 0, color: '#0f172a', fontWeight: 700, lineHeight: 1.35, fontSize: 'clamp(0.95rem, 2vw, 1.05rem)' }}>
          {title}
        </Title>
        {loading
          ? (
          <Skeleton active paragraph={{ rows: 1 }} title={false} style={{ width: '100%', maxWidth: 200 }} />
            )
          : (
          <span
            style={{
              fontSize: 'clamp(1.05rem, 2.8vw, 1.4rem)',
              fontWeight: 800,
              color: c.value,
              letterSpacing: '-0.02em',
              lineHeight: 1.2
            }}
          >
            {value}
          </span>
            )}
        {!loading && showProgress && (
          <div style={{ width: '100%', maxWidth: 232, marginTop: 4 }}>
            <Progress
              percent={pct}
              showInfo={false}
              size="small"
              strokeColor={c.progress}
              trailColor="rgba(226, 232, 240, 0.9)"
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginTop: 6, gap: 8 }}>
              <Text type="secondary" style={{ fontSize: 12, textAlign: 'left', flex: 1, lineHeight: 1.35 }}>
                {percentHint}
              </Text>
              <Text strong style={{ fontSize: 13, color: c.icon, whiteSpace: 'nowrap' }}>
                {pct.toFixed(2)}
                %
              </Text>
            </div>
          </div>
        )}
        {!loading && !showProgress && (
          <Text type="secondary" style={{ fontSize: 12, marginTop: 6, lineHeight: 1.45, maxWidth: 260 }}>
            {percentHint}
          </Text>
        )}
        {action != null && (
          <div style={{ marginTop: 'auto', paddingTop: 10, width: '100%' }}>
            {action}
          </div>
        )}
      </div>
    </Card>
  )
}

const FinanceDashboard: React.FC = () => {
  const navigate = useNavigate()
  const [stats, setStats] = React.useState<Record<string, any>>({})
  const [refundRequestList, setRefundRequestList] = useState<DataType[]>([])
  const [statsRefund, setStatsRefund] = React.useState<Record<string, any>>({})
  const [statsBailiffRefund, setStatsBailiffRefund] = React.useState<Record<string, any>>({})
  const [totalAmount, setTotalAmount] = useState(0)
  const [totalPaid, setTotalPaidAmount] = useState(0)
  const [certifiedCheckAmount, setCertifiedCheckAmount] = useState(0)
  const [bidGuaranteeAmount, setBidGuaranteeAmount] = useState(0)
  const [digitalPaymentAmount, setDigitalPaymentAmount] = useState(0)
  const [modalVisible, setModalVisible] = useState(false)
  const [trendModal, setTrendModal] = useState<FinanceKpiTrendKey | null>(null)
  const [currentTime, setCurrentTime] = useState(new Date())
  const [bidderAuto, setBidderAuto] = useState(false)
  const [bailiffAuto, setBailiffAuto] = useState(false)

  useEffect(() => {
    Promise.all([
      InstitutionUserService.getSystemSettingByKey('BIDDER_AUTO_REFUND_ENABLED')
        .then((res: { status: number, data?: { setting_value: string } }) => {
          if (res.status === 200 && res.data !== undefined && res.data !== null) {
            setBidderAuto(res.data.setting_value === 'true')
          }
        }),
      InstitutionUserService.getSystemSettingByKey('BAILIFF_AUTO_REFUND_ENABLED')
        .then((res: { status: number, data?: { setting_value: string } }) => {
          if (res.status === 200 && res.data !== undefined && res.data !== null) {
            setBailiffAuto(res.data.setting_value === 'true')
          }
        })
    ]).catch((err: unknown) => {
      // eslint-disable-next-line no-console
      console.error('Failed to load auto-refund configuration:', err)
      setBidderAuto(false)
      setBailiffAuto(false)
    })
  }, [])

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const toggleModal = (): void => {
    setModalVisible(!modalVisible)
  } // Added closing parenthesis here

  const kpi = useMemo(() => {
    if (
      statsRefund.totalRefundPayments === undefined ||
      statsBailiffRefund.totalPayments === undefined
    ) {
      return null
    }
    const r = (x: unknown): number => {
      const n = parseInt(String(x ?? '0'), 10)
      return Number.isFinite(n) ? n : 0
    }
    const totalRefunded = r(statsRefund.totalRefundPayments) + r(statsBailiffRefund.totalPayments)
    const pending = r(statsRefund.amountRequestedToBeRefundedInProgressStatus) + r(statsBailiffRefund.amountRequestedToBeRefundedInProgressStatus)
    const requested = r(statsRefund.amountRequestedToBeRefund) + r(statsBailiffRefund.amountRequestedToBeRefund)
    const paidSafe = totalPaid > 0 ? totalPaid : 0
    const refundedPct = paidSafe > 0 ? (totalRefunded / paidSafe) * 100 : 0
    const requestedPct = paidSafe > 0 ? (requested / paidSafe) * 100 : 0
    const pendingPct = requested > 0 ? (pending / requested) * 100 : 0
    return {
      totalRefunded,
      pending,
      requested,
      refundedPct,
      requestedPct,
      pendingPct
    }
  }, [statsRefund, statsBailiffRefund, totalPaid])

  const financialBreakdown = useMemo(() => {
    const safe = (v: unknown): number => {
      const n = parseInt(String(v ?? '0'), 10)
      return Number.isFinite(n) ? n : 0
    }
    const certified = safe(stats?.certified_checks)
    const digital = safe(stats?.payments?.totalPayments)
    const guarantees = safe(stats?.bid_guarantees)
    const sum = certified + digital + guarantees
    const pct = (part: number): number => (sum > 0 ? (part / sum) * 100 : 0)
    return {
      sum,
      rows: [
        {
          key: 'cc',
          label: 'Certified checks',
          shortLabel: 'Document-based',
          amount: certified,
          accent: 'emerald' as KpiAccentKey,
          pct: pct(certified),
          icon: <FileTextOutlined />
        },
        {
          key: 'dp',
          label: 'Digital payments',
          shortLabel: 'Online / transfers',
          amount: digital,
          accent: 'indigo' as KpiAccentKey,
          pct: pct(digital),
          icon: <CreditCardOutlined />
        },
        {
          key: 'bg',
          label: 'Bid guarantees',
          shortLabel: 'Bank guarantees',
          amount: guarantees,
          accent: 'forest' as KpiAccentKey,
          pct: pct(guarantees),
          icon: <BankOutlined />
        }
      ]
    }
  }, [stats])

  const chartRefundMatrix = stats.chartRefundData as Array<Array<string | number>> | undefined

  const trendRefundedChart = useMemo(
    () => buildFinanceKpiSingleSeriesTrend(chartRefundMatrix, 2, 'Amount refunded (RWF)'),
    [chartRefundMatrix]
  )

  const trendRequestedChart = useMemo(
    () => buildFinanceKpiSingleSeriesTrend(chartRefundMatrix, 1, 'Amount requested (RWF)'),
    [chartRefundMatrix]
  )

  const trendPipelineChart = useMemo(
    () => buildFinanceKpiPipelineCompareTrend(chartRefundMatrix),
    [chartRefundMatrix]
  )

  const trendModalChartConfig = useMemo(() => {
    if (trendModal === null) return null
    const data =
      trendModal === 'refunded'
        ? trendRefundedChart
        : trendModal === 'requested'
          ? trendRequestedChart
          : trendPipelineChart
    return {
      data,
      chartType: 'ColumnChart' as const,
      options: buildFinanceKpiColumnOptions(trendModal, 'modal')
    }
  }, [trendModal, trendRefundedChart, trendRequestedChart, trendPipelineChart])

  useEffect(() => {
    // Calculate total amount whenever refundRequestList changes
    const totalPaidAmount = certifiedCheckAmount + bidGuaranteeAmount + digitalPaymentAmount
    setTotalPaidAmount(totalPaidAmount)
  }, [certifiedCheckAmount, bidGuaranteeAmount, digitalPaymentAmount])

  useEffect(() => {
    const total = refundRequestList.reduce(
      (acc, curr) => acc + Number(curr.amount ?? 0),
      0
    )
    setTotalAmount(total)
  }, [refundRequestList])

  React.useEffect(() => {
    InstitutionUserService.RefundRequestStats()
      .then((res: any) => {
        setStatsRefund(res.data)
      })
      .catch((err) => {
        void message.error(err?.message)
      })

    InstitutionUserService.bailiffRefundRequestStats()
      .then((response: any) => {
        setStatsBailiffRefund(response.data)
      })
      .catch((err) => {
        void message.error(err?.message)
      })
  }, [])

  React.useEffect(() => {
    InstitutionUserService.MFinanceRefundStats()
      .then((res: any) => {
        const chartRefundData = generateChartRefundDataMFinance(res?.data.barData)

        setStats((prev) => ({
          ...prev,
          ...res?.data,
          chartRefundData
        }))
      })
      .catch((err) => {
        void message.error(err?.message)
      })
  }, [])

  useEffect(() => {
    InstitutionUserService.MFinanceStats()
      .then((res: any) => {
        const chartData = generateChartDataMFinance(res?.data.barData, 'documents')
        setStats((prev) => ({
          ...prev,
          ...res?.data,
          chartData
        }))
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const certifiedCheckAmount = parseInt(res?.data?.certified_checks)
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const bidGuaranteeAmount = parseInt(res?.data?.bid_guarantees)
        // eslint-disable-next-line @typescript-eslint/no-shadow
        const digitalPaymentAmount = parseInt(res?.data?.payments?.totalPayments)

        setCertifiedCheckAmount(isNaN(certifiedCheckAmount) ? 0 : certifiedCheckAmount)
        setBidGuaranteeAmount(isNaN(bidGuaranteeAmount) ? 0 : bidGuaranteeAmount)
        setDigitalPaymentAmount(isNaN(digitalPaymentAmount) ? 0 : digitalPaymentAmount)
      })
      .catch((err) => {
        void message.error(err?.message)
      })
  }, [])

  // Format date
  const formatDate = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    }
    return date.toLocaleDateString(undefined, options)
  }

  // Format time
  const formatTime = (date: Date): string => {
    const options: Intl.DateTimeFormatOptions = {
      hour: '2-digit',
      minute: '2-digit',
      hour12: true
    }
    return date.toLocaleTimeString(undefined, options)
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  const generateCalendar = () => {
    const year = currentTime.getFullYear()
    const month = currentTime.getMonth()
    const today = currentTime.getDate()

    // Day names
    const dayNames = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']

    // Month name
    const monthNames = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ]

    // Create calendar grid
    const calendarDays = []

    // Add day headers
    dayNames.forEach(day => {
      calendarDays.push(
        <div key={`header-${day}`} className="text-center text-xs font-medium py-1">
          {day}
        </div>
      )
    })

    // Get the current day of week (0-6)
    const currentDayOfWeek = new Date(year, month, today).getDay()

    // Calculate start date (go back to find Sunday of the week before)
    // This ensures we show the current week plus one week before and one after
    const startDay = today - currentDayOfWeek - 7

    // We'll show 3 weeks (21 days) centered around current week
    for (let i = 0; i < 21; i++) {
      const displayDay = startDay + i
      const displayDate = new Date(year, month, displayDay)
      const dayOfMonth = displayDate.getDate()
      const isCurrentMonth = displayDate.getMonth() === month
      const isToday = isCurrentMonth && dayOfMonth === today

      calendarDays.push(
        <div
          key={`day-${i}`}
          className={`text-center py-1 text-sm ${
            isToday
              ? 'bg-blue-500 text-white rounded-full font-bold'
              : isCurrentMonth
                ? ''
                : 'text-gray-300'
          }`}
        >
          {dayOfMonth}
        </div>
      )
    }

    return (
      <div className="mt-2">
        <div className="text-center font-medium mb-2">
          {monthNames[month]} {year}
        </div>
        <div className="grid grid-cols-7 gap-1">
          {calendarDays}
        </div>
      </div>
    )
  }

  return (
    <div className="w-full finance-dashboard-modern" style={{ width: '100%' }}>
      {/* Add custom styles */}
      <style>{`
        .enhanced-card {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .enhanced-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
        .enhanced-button {
          border-radius: 8px;
          font-weight: 600;
          transition: all 0.2s ease;
        }
        .enhanced-button:hover {
          transform: translateY(-1px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
        }
        .stats-card {
          transition: all 0.3s ease;
          cursor: pointer;
        }
        .stats-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 30px rgba(0, 0, 0, 0.15);
        }
        .chart-card {
          background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
          border: 1px solid #e5e7eb;
          border-radius: 12px;
          box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
          transition: all 0.2s ease;
        }
        .chart-card:hover {
          box-shadow: 0 8px 25px rgba(0, 0, 0, 0.1);
          transform: translateY(-2px);
        }
      `}</style>

      {/* Enhanced Header Section */}
      <div className="px-5 py-4 border-b-slate-200 border border-solid w-full finance-dashboard-header" style={{
        background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
        borderRadius: '0 0 12px 12px',
        marginBottom: '20px'
      }}>
        <div className="flex flex-col lg:flex-row gap-4 lg:items-start">
          {/* Enhanced Welcome Section */}
          <div className="text-base font-bold w-full">
            <div className="flex items-center justify-center lg:justify-start mb-4">
              <div style={{
                background: 'var(--refund-theme-primary-soft)',
                borderRadius: '50%',
                width: '48px',
                height: '48px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                marginRight: '16px',
                boxShadow: '0 4px 12px rgba(55, 118, 47, 0.3)'
              }}>
                <DollarCircleOutlined style={{ fontSize: '24px', color: '#111827' }} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <h2 className="text-title font-bold" style={{
                  margin: 0,
                  background: 'linear-gradient(135deg, #37762f 0%, #4a7c59 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text'
                }}>
                  Finance Dashboard
                </h2>
                <p style={{
                  margin: '4px 0 0 0',
                  fontSize: '14px',
                  color: '#6b7280',
                  fontWeight: '500'
                }}>
                  Financial Overview • Analytics Dashboard
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Dashboard Cards Section */}
      <div className="p-3 sm:p-5 md:p-7">
        {/* Automated Refund Configuration Switch */}
        <Card
          bordered={false}
          className="mb-6 rounded-2xl overflow-hidden"
          style={{
            background: 'rgba(255, 255, 255, 0.75)',
            backdropFilter: 'blur(12px)',
            border: '1px solid rgba(226, 232, 240, 0.8)',
            boxShadow: '0 4px 20px -2px rgba(15, 23, 42, 0.05)',
            borderRadius: '16px'
          }}
          bodyStyle={{ padding: '16px 24px' }}
        >
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4">
              <div
                style={{
                  background: (bidderAuto || bailiffAuto)
                    ? 'linear-gradient(135deg, #ecfdf5 0%, #d1fae5 100%)'
                    : 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)',
                  borderRadius: '12px',
                  width: '40px',
                  height: '40px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  border: (bidderAuto || bailiffAuto)
                    ? '1.5px solid rgba(16, 185, 129, 0.2)'
                    : '1.5px solid rgba(148, 163, 184, 0.2)',
                  color: (bidderAuto || bailiffAuto) ? '#059669' : '#64748b',
                  fontSize: '18px',
                  flexShrink: 0
                }}
              >
                <SettingOutlined />
              </div>
              <div style={{ textAlign: 'left' }}>
                <h3
                  className="font-bold text-gray-800 m-0"
                  style={{ fontSize: '15px', color: '#1e293b' }}
                >
                  Refund Payout Preferences Configuration
                </h3>
                <div className="flex flex-wrap items-center gap-3 mt-1.5">
                  <span className="text-gray-500 text-xs font-medium" style={{ color: '#64748b' }}>
                    Bidders:
                  </span>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: bidderAuto ? '#d1fae5' : '#f1f5f9',
                      color: bidderAuto ? '#065f46' : '#475569'
                    }}
                  >
                    {bidderAuto ? 'Auto Payout' : 'Manual Payout'}
                  </span>
                  <span className="text-gray-500 text-xs font-medium" style={{ color: '#64748b', marginLeft: '6px' }}>
                    Bailiffs:
                  </span>
                  <span
                    className="text-xs font-bold px-2 py-0.5 rounded-full"
                    style={{
                      backgroundColor: bailiffAuto ? '#d1fae5' : '#f1f5f9',
                      color: bailiffAuto ? '#065f46' : '#475569'
                    }}
                  >
                    {bailiffAuto ? 'Auto Payout' : 'Manual Payout'}
                  </span>
                </div>
              </div>
            </div>
            <Button
              type="default"
              onClick={() => {
                navigate('/refund-configuration')
              }}
              style={{
                borderRadius: '8px',
                fontWeight: 600,
                borderColor: '#e2e8f0',
                color: '#334155'
              }}
            >
              Configure Mode
            </Button>
          </div>
        </Card>

        <div style={{ marginBottom: 20 }}>
          <Title level={4} style={{ margin: 0, color: '#0f172a', fontWeight: 700 }}>
            At a glance
          </Title>
          <Text type="secondary" style={{ fontSize: 14, display: 'block', marginTop: 8, maxWidth: 720, lineHeight: 1.5 }}>
            Refund and bid-security KPIs update from live statistics. Each percentage explains how the amount compares to a reference total (see the note under the bar).
          </Text>
        </div>

        <Row gutter={[16, 16]} className="mb-6 finance-dashboard-stats-row">
          <Col xs={24} sm={12} lg={6}>
            <FinanceKpiStatCard
              accent="emerald"
              icon={<DollarCircleOutlined />}
              category="Refunds"
              title="Total refunded"
              value={kpi !== null ? `${kpi.totalRefunded.toLocaleString()} Rwf` : '—'}
              percent={kpi?.refundedPct ?? 0}
              percentHint="Of total bid security paid (all channels)"
              loading={kpi === null}
              action={(
                <Popover
                  mouseEnterDelay={0.4}
                  placement="bottom"
                  trigger="hover"
                  overlayInnerStyle={{ padding: 12, maxWidth: 400 }}
                  content={(
                    <div style={{ width: 360, maxWidth: 'min(92vw, 380px)' }}>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 10, lineHeight: 1.45 }}>
                        Hover preview — click the button for a larger chart.
                      </Text>
                      <GoogleChartOrEmpty
                        chartType="ColumnChart"
                        width="100%"
                        height="220px"
                        data={trendRefundedChart}
                        options={buildFinanceKpiColumnOptions('refunded', 'popover')}
                        emptyLabel="No monthly data yet."
                      />
                    </div>
                  )}
                >
                  <span style={{ display: 'block', width: '100%' }}>
                    <Button
                      type="default"
                      size="small"
                      block
                      icon={<BarChartOutlined />}
                      onClick={() => { setTrendModal('refunded') }}
                      style={{ borderRadius: 8, fontWeight: 600 }}
                    >
                      View trends
                    </Button>
                  </span>
                </Popover>
              )}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FinanceKpiStatCard
              accent="amber"
              icon={<ClockCircleOutlined />}
              category="Pipeline"
              title="Pending refund requests"
              value={kpi !== null ? `${kpi.pending.toLocaleString()} Rwf` : '—'}
              percent={kpi?.pendingPct ?? 0}
              percentHint="Of all requested refund volume (in-progress share)"
              loading={kpi === null}
              action={(
                <Popover
                  mouseEnterDelay={0.4}
                  placement="bottom"
                  trigger="hover"
                  overlayInnerStyle={{ padding: 12, maxWidth: 400 }}
                  content={(
                    <div style={{ width: 360, maxWidth: 'min(92vw, 380px)' }}>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 10, lineHeight: 1.45 }}>
                        Hover preview — click the button for a larger chart.
                      </Text>
                      <GoogleChartOrEmpty
                        chartType="ColumnChart"
                        width="100%"
                        height="220px"
                        data={trendPipelineChart}
                        options={buildFinanceKpiColumnOptions('pipeline', 'popover')}
                        emptyLabel="No monthly data yet."
                      />
                    </div>
                  )}
                >
                  <span style={{ display: 'block', width: '100%' }}>
                    <Button
                      type="default"
                      size="small"
                      block
                      icon={<BarChartOutlined />}
                      onClick={() => { setTrendModal('pipeline') }}
                      style={{ borderRadius: 8, fontWeight: 600 }}
                    >
                      View trends
                    </Button>
                  </span>
                </Popover>
              )}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FinanceKpiStatCard
              accent="indigo"
              icon={<FileTextOutlined />}
              category="Requests"
              title="Total requested for refund"
              value={kpi !== null ? `${kpi.requested.toLocaleString()} Rwf` : '—'}
              percent={kpi?.requestedPct ?? 0}
              percentHint="Of total bid security paid (all channels)"
              loading={kpi === null}
              action={(
                <Popover
                  mouseEnterDelay={0.4}
                  placement="bottom"
                  trigger="hover"
                  overlayInnerStyle={{ padding: 12, maxWidth: 400 }}
                  content={(
                    <div style={{ width: 360, maxWidth: 'min(92vw, 380px)' }}>
                      <Text type="secondary" style={{ display: 'block', fontSize: 12, marginBottom: 10, lineHeight: 1.45 }}>
                        Hover preview — click the button for a larger chart.
                      </Text>
                      <GoogleChartOrEmpty
                        chartType="ColumnChart"
                        width="100%"
                        height="220px"
                        data={trendRequestedChart}
                        options={buildFinanceKpiColumnOptions('requested', 'popover')}
                        emptyLabel="No monthly data yet."
                      />
                    </div>
                  )}
                >
                  <span style={{ display: 'block', width: '100%' }}>
                    <Button
                      type="default"
                      size="small"
                      block
                      icon={<BarChartOutlined />}
                      onClick={() => { setTrendModal('requested') }}
                      style={{ borderRadius: 8, fontWeight: 600 }}
                    >
                      View trends
                    </Button>
                  </span>
                </Popover>
              )}
            />
          </Col>
          <Col xs={24} sm={12} lg={6}>
            <FinanceKpiStatCard
              accent="forest"
              icon={<BankOutlined />}
              category="Bid security"
              title="Total amount paid"
              value={`${totalPaid.toLocaleString()} Rwf`}
              percent={null}
              percentHint="Certified checks, bid guarantees, and digital payments combined."
              loading={false}
              showProgress={false}
              action={(
                <Button
                  type="primary"
                  size="small"
                  block
                  icon={<BarChartOutlined />}
                  onClick={toggleModal}
                  className="enhanced-button"
                  style={{ borderRadius: 8, fontWeight: 600, height: 36 }}
                >
                  View breakdown
                </Button>
              )}
            />
          </Col>
        </Row>

        {/* Enhanced Charts Row */}
        <Row id="finance-dashboard-charts" gutter={[12, 12]} className="finance-dashboard-charts-row">
          <Col xs={24} lg={12}>
            <Card
              className="chart-card h-full finance-chart-card"
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="text-center">
                <div style={{
                  background: 'var(--refund-theme-primary-soft)',
                  color: '#111827',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <BarChartOutlined />
                  Bid Security Provided Per Month
                </div>
                <GoogleChartOrEmpty
                  chartType="Line"
                  width="100%"
                  height="300px"
                  data={stats?.chartData}
                  options={{
                    ...barOptions,
                    responsive: true,
                    maintainAspectRatio: false,
                    backgroundColor: 'transparent',
                    chartArea: {
                      backgroundColor: 'transparent'
                    }
                  }}
                />
              </div>
            </Card>
          </Col>

          <Col xs={24} lg={12}>
            <Card
              className="chart-card h-full finance-chart-card"
              style={{
                background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
                borderRadius: '12px',
                border: '1px solid #e5e7eb',
                boxShadow: '0 4px 6px rgba(0, 0, 0, 0.05)'
              }}
            >
              <div className="text-center">
                <div style={{
                  background: 'var(--refund-theme-primary-soft)',
                  color: '#111827',
                  padding: '10px 16px',
                  borderRadius: '8px',
                  fontWeight: '600',
                  fontSize: '15px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  marginBottom: '16px'
                }}>
                  <LineChartOutlined />
                  Digital Refunded Amounts per Month
                </div>
                <GoogleChartOrEmpty
                  chartType="Line"
                  width="100%"
                  height="300px"
                  data={stats?.chartRefundData}
                  options={{
                    ...barOptions2,
                    responsive: true,
                    maintainAspectRatio: false,
                    backgroundColor: 'transparent',
                    chartArea: {
                      backgroundColor: 'transparent'
                    }
                  }}
                />
              </div>
            </Card>
          </Col>
        </Row>
      </div>

      {/* KPI card — View trends */}
      <Modal
        open={trendModal !== null}
        onCancel={() => { setTrendModal(null) }}
        destroyOnClose
        className="paymentsStats finance-dashboard-modal finance-kpi-trend-modal"
        width={900}
        style={{ maxWidth: '96vw' }}
        bodyStyle={{ padding: '16px 20px 20px' }}
        footer={(
          <div className="finance-breakdown-modal-footer">
            <Button type="primary" className="enhanced-button" onClick={() => { setTrendModal(null) }}>
              Close
            </Button>
          </div>
        )}
        title={
          trendModal !== null
            ? (
            <span style={{ fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
              {TREND_MODAL_META[trendModal].title}
            </span>
              )
            : ''
        }
      >
        {trendModal !== null && (
          <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 16, fontSize: 14, lineHeight: 1.55 }}>
              {TREND_MODAL_META[trendModal].description}
            </Text>
            {trendModalChartConfig !== null && (
              <GoogleChartOrEmpty
                chartType={trendModalChartConfig.chartType}
                width="100%"
                height="400px"
                data={trendModalChartConfig.data}
                options={trendModalChartConfig.options}
                emptyLabel="No monthly refund trend data yet. Wait for statistics to load or check your connection."
              />
            )}
          </>
        )}
      </Modal>

      {/* Financial breakdown modal */}
      <Modal
        open={modalVisible}
        onCancel={toggleModal}
        className="paymentsStats finance-dashboard-modal finance-breakdown-modal"
        width={720}
        style={{
          maxWidth: '94vw',
          borderRadius: '12px',
          background: 'white'
        }}
        bodyStyle={{
          background: 'linear-gradient(180deg, #fafafa 0%, #ffffff 40%)',
          padding: '20px 24px 16px'
        }}
        footer={(
          <div className="finance-breakdown-modal-footer">
            <Space size={10}>
              <Button size="middle" onClick={toggleModal}>
                Cancel
              </Button>
              <Button type="primary" size="middle" className="enhanced-button" onClick={toggleModal}>
                OK
              </Button>
            </Space>
          </div>
        )}
        title={(
          <div className="finance-breakdown-modal-title">
            <span className="finance-breakdown-modal-title__icon" aria-hidden>
              <BarChartOutlined />
            </span>
            <span className="finance-breakdown-modal-title__text">
              <span className="finance-breakdown-modal-title__headline">Detailed financial breakdown</span>
              <Text type="secondary" className="finance-breakdown-modal-title__sub">
                How total bid security paid splits across channels (same figures as the dashboard).
              </Text>
            </span>
          </div>
        )}
      >
        <Text type="secondary" style={{ display: 'block', marginBottom: 18, fontSize: 14, lineHeight: 1.55 }}>
          Each card shows amount in RWF and its share of the combined total in the progress bar. Use this to compare certified checks, digital payments, and bid guarantees at a glance.
        </Text>

        <div className="finance-breakdown-grid">
          {financialBreakdown.rows.map((row) => {
            const c = KPI_ACCENTS[row.accent]
            return (
              <div
                key={row.key}
                className={`finance-breakdown-card finance-breakdown-card--${row.accent}`}
              >
                <div
                  className="finance-breakdown-card__icon-wrap"
                  style={{
                    background: c.iconBg,
                    border: `2px solid ${c.ring}`
                  }}
                >
                  <span style={{ display: 'inline-flex', color: c.icon, fontSize: 22 }}>{row.icon}</span>
                </div>
                <Text className="finance-breakdown-card__eyebrow">{row.shortLabel}</Text>
                <Text strong className="finance-breakdown-card__label">{row.label}</Text>
                <span
                  className="finance-breakdown-card__amount"
                  style={{ color: c.value }}
                >
                  {row.amount.toLocaleString()}
                  {' '}
                  <span className="finance-breakdown-card__currency">RWF</span>
                </span>
                <div className="finance-breakdown-card__progress">
                  <Progress
                    percent={Number(row.pct.toFixed(2))}
                    showInfo={false}
                    size="small"
                    strokeColor={c.progress}
                    trailColor="rgba(226, 232, 240, 0.95)"
                  />
                </div>
                <Text type="secondary" className="finance-breakdown-card__pct-caption">
                  {row.pct.toFixed(1)}
                  % of combined total
                </Text>
              </div>
            )
          })}
        </div>

        <div className="finance-breakdown-total">
          <div className="finance-breakdown-total__label">
            <Text strong style={{ fontSize: 15, color: '#0f172a' }}>Combined total</Text>
            <Text type="secondary" style={{ display: 'block', fontSize: 12, marginTop: 2 }}>
              Certified checks + digital payments + bid guarantees
            </Text>
          </div>
          <div className="finance-breakdown-total__value" style={{ color: '#1e4620' }}>
            {financialBreakdown.sum.toLocaleString()}
            {' '}
            <span className="finance-breakdown-card__currency">RWF</span>
          </div>
        </div>
      </Modal>

    </div>
  )
}

export default FinanceDashboard
